import React, { useState, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Modal, Button, Form, ToggleButton, ToggleButtonGroup, Row, Col } from 'react-bootstrap';

import { useAxios } from 'configs/requests';
import { formatDropdownOptions } from 'utils/Object';
import { unitClasses } from 'constants/constants';
import { Error, Loading } from 'components/Fetch';
import { SettingsGreyIcon } from 'assets/icons';
import { SectionTitle } from 'components/UI';
import { Dropdown } from 'components/Forms';
import { getUnitPreferences, setUnitPreferences } from 'store/settings/reducer';
import { getTimeLogTypes, selectGroups } from 'store/groups/reducer';
import { setLocale } from 'store/intl/reducer';
import { getUserTimeLogType, getUserAutoTimeLog, setTimeLogPrefs } from 'store/settings/reducer';
import { Locales } from 'constants/enums';
import { getMeasurementsUnits } from 'utils/Measurements';
import { generateUUID } from 'utils/Helpers/UUID';
import { useAppSelector } from 'hooks';

const Settings = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const citc = useAppSelector((state) => state.settings.citc);
  const languages = useAppSelector((state) => state.intl.languages);
  const locale = useAppSelector((state) => state.intl.locale);
  const timeLogTypes = useAppSelector(getTimeLogTypes);
  const unitPreferences = useAppSelector((state) => getUnitPreferences(state));
  const measurementClasses =
    useAppSelector((state) => state.settings.citc?.measurementClasses) || [];
  const unitOptions = getMeasurementsUnits(measurementClasses);
  const aid = useAppSelector((state) => state.oauth.data?.aid);
  const defaultClinicalTimeLogTypeId = useAppSelector(getUserTimeLogType);
  const automaticClinicalTimeLog = useAppSelector(getUserAutoTimeLog);
  const groups = useAppSelector(selectGroups);
  const hasClinicalTimeLogging = groups.some(
    (group: IGroupInfo) => group.gid !== 'all' && !group.timeLogsDisabled
  );

  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const [newLocale, setNewLocale] = useState(locale);
  const [newAutoTimeLogPref, setNewAutoTimeLogPref] = useState(automaticClinicalTimeLog);
  const [newTimeLogTypePref, setNewTimeLogTypePref] = useState(defaultClinicalTimeLogTypeId);
  const [newUnitPrefs, setNewUnitPrefs] = useState<IUnitPreference>(unitPreferences);
  const [saved, setSaved] = useState<boolean>(false);

  useEffect(() => {
    setNewUnitPrefs(unitPreferences);
    setNewAutoTimeLogPref(automaticClinicalTimeLog);
    setNewTimeLogTypePref(defaultClinicalTimeLogTypeId);
  }, [unitPreferences, automaticClinicalTimeLog, defaultClinicalTimeLogTypeId]);

  const [{ error: localeError, loading: localeLoading }, postLocale] =
    useAxios<ICompletePreferenceBundle>(
      { url: '/clinician/Account/Preferences', method: 'POST' },
      { manual: true }
    );

  const [{ error: settingsError, loading: settingsLoading }, postAccountAttributes] =
    useAxios<IAccountAttributeBundle>(
      { url: `/clinician-gateway/accounts/${aid}/attributes`, method: 'PATCH' },
      { manual: true }
    );

  const locales = (citc?.locales || []).filter(
    (locale) => locale !== Locales.zhHans && locale !== Locales.zhHant
  );
  const languageOptsForDropdown: ISelectOption[] = locales.map((locale) => {
    return {
      label: languages[locale],
      value: locale
    };
  });

  const timeLogTypesOptions = timeLogTypes
    ? formatDropdownOptions(timeLogTypes, 'timeLogTypeId', 'labelKey').map((task) => {
        return {
          ...task,
          label: intl.formatMessage({
            id: task.label
          })
        };
      })
    : [];

  const unitOptsForDropdown = {} as { [key: string]: ISelectOption[] };
  Object.keys(unitPreferences).forEach((unitClass: string) => {
    if (unitClasses.includes(unitClass) && citc) {
      const unitsForClass =
        unitOptions[unitClass === 'glucose' ? 'glucose_unspecified' : unitClass];
      if (unitsForClass) {
        unitOptsForDropdown[unitClass] = unitsForClass.map((unit) => {
          return {
            value: unit,
            label: intl.formatMessage({ id: unit, defaultMessage: unit })
          } as ISelectOption;
        });
      }
    }
  });

  const autoTimeLogOptions = [
    { name: intl.formatMessage({ id: 'onKey' }), value: '1' },
    { name: intl.formatMessage({ id: 'OFF' }), value: '0' }
  ];

  const handleCloseModal = () => {
    // reset form to original values on close or cancel
    setNewLocale(locale);
    setNewAutoTimeLogPref(automaticClinicalTimeLog);
    setNewTimeLogTypePref(defaultClinicalTimeLogTypeId);
    setNewUnitPrefs(unitPreferences);
    setShowSettingsModal(false);
    setSaved(false);
  };

  const handleSubmit = async () => {
    if (newLocale !== locale) {
      await postLocale({
        data: {
          locale: newLocale,
          accountPreferences: []
        }
      });
      dispatch(setLocale(newLocale));
    }

    const attributes = [];
    if (unitPreferences !== newUnitPrefs) {
      attributes.push({
        attribute: 'unitPreferences',
        value: JSON.stringify(newUnitPrefs)
      });
    }

    if (newTimeLogTypePref !== defaultClinicalTimeLogTypeId) {
      attributes.push({
        attribute: 'defaultClinicalTimeLogTypeId',
        value: newTimeLogTypePref
      });
    }

    if (newAutoTimeLogPref !== automaticClinicalTimeLog) {
      attributes.push({
        attribute: 'automaticClinicalTimeLog',
        value: newAutoTimeLogPref.toString()
      });
    }

    if (attributes.length > 0) {
      await postAccountAttributes({
        data: { attributes: attributes }
      });
    }

    setShowSettingsModal(false);
    newUnitPrefs !== unitPreferences && dispatch(setUnitPreferences({ preferences: newUnitPrefs }));
    newLocale !== locale && dispatch(setLocale(newLocale));
    if (
      newTimeLogTypePref !== defaultClinicalTimeLogTypeId ||
      newAutoTimeLogPref !== automaticClinicalTimeLog
    ) {
      dispatch(
        setTimeLogPrefs({
          defaultClinicalTimeLogTypeId: newTimeLogTypePref,
          automaticClinicalTimeLog: newAutoTimeLogPref
        })
      );
    }
  };

  const handleNewLocale = (locale: LocalesKeys) => {
    setNewLocale(locale);
    setSaved(false);
  };

  const handleNewTimeLogType = (type: string) => {
    setNewTimeLogTypePref(type);
    setSaved(false);
  };

  const handleNewAutoTimeLog = (autoTimeLog: string) => {
    setNewAutoTimeLogPref(autoTimeLog === '1');
    setSaved(false);
  };

  const handleSelectUnit = (unit: string, measureClass: string) => {
    setNewUnitPrefs((prevState: IUnitPreference) => {
      const newBundle = { ...prevState, [measureClass]: unit };
      return newBundle;
    });
    setSaved(false);
  };

  return (
    <>
      <SettingsGreyIcon
        data-test="settings-btn"
        onClick={() => setShowSettingsModal(true)}
        className="icon iconTopNav"
      />
      <Modal
        show={showSettingsModal}
        onHide={handleCloseModal}
        backdrop={true}
        data-test="settings-modal"
        centered
      >
        <Modal.Header closeButton className="">
          <Modal.Title data-test="settings-title">
            {intl.formatMessage({ id: 'Settings' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(settingsError || localeError) && (
            <div className="text-center mt-3 text-danger">
              <Error errorMessage={intl.formatMessage({ id: 'preferencesErrorKey' })} />
            </div>
          )}
          <Form className="mx-4">
            <SectionTitle label={intl.formatMessage({ id: 'languageTitle' })} size="lg" />
            <Row className="mb-4 d-flex align-items-center">
              <Col sm={8}>
                <Form.Label className="font-weight-normal mb-0">
                  {intl.formatMessage({ id: 'selectLanguageKey' })}
                </Form.Label>
              </Col>
              <Col sm={4}>
                <Dropdown
                  value={languageOptsForDropdown.find((option) => option.value === newLocale)}
                  options={languageOptsForDropdown}
                  variant="form"
                  dataTestId="settings-language-dropdown"
                  className="w-175"
                  isSearchable={false}
                  onChange={(option: ISelectOption) => handleNewLocale(option.value as LocalesKeys)}
                />
              </Col>
            </Row>
            {hasClinicalTimeLogging && (
              <div data-test="settings-time-logging-section">
                <SectionTitle
                  label={intl.formatMessage({ id: 'clinicalTimeLoggingTitleKey' })}
                  size="lg"
                />
                <Row className="mb-2 d-flex align-items-center">
                  <Col sm={8}>
                    <Form.Label className="font-weight-normal mb-0">
                      {intl.formatMessage({ id: 'defaultClinicalTimeLogType' })}
                    </Form.Label>
                  </Col>
                  <Col sm={4}>
                    <Dropdown
                      options={timeLogTypesOptions}
                      value={timeLogTypesOptions.find(
                        (option) => option.value === newTimeLogTypePref
                      )}
                      variant="form"
                      dataTestId="settings-billing-dropdown"
                      className="w-175"
                      isSearchable={false}
                      limitHeight
                      onChange={(type: ISelectOption) => handleNewTimeLogType(type.value)}
                    />
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Form.Label column className="font-weight-normal align-self-center">
                    {intl.formatMessage({ id: 'enableAutomaticClinicalTimeLog' })}
                  </Form.Label>
                  <ToggleButtonGroup
                    name="autoTimeLog"
                    type="radio"
                    data-test="settings-auto-time-toggle"
                    className="w-175"
                    value={newAutoTimeLogPref ? '1' : '0'}
                    onChange={(autoTimeLog: string) => handleNewAutoTimeLog(autoTimeLog)}
                  >
                    {autoTimeLogOptions.map((option, idx) => (
                      <ToggleButton
                        id={generateUUID()}
                        key={idx}
                        variant={idx % 2 ? 'outline-secondary' : 'outline-primary'}
                        name="radio"
                        data-test={`settings-auto-time-toggle-${idx}`}
                        value={option.value}
                        checked={newAutoTimeLogPref === (option.value === '1')}
                        size="sm"
                        className="align-self-center h-fit-content py-2"
                        disabled={!newTimeLogTypePref}
                      >
                        {option.name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Row>
              </div>
            )}

            <SectionTitle label={intl.formatMessage({ id: 'Units' })} size="lg" />
            {Object.keys(unitPreferences).map((measureClass, index) => {
              return (
                unitOptsForDropdown[measureClass] && (
                  <Row
                    key={measureClass}
                    className={`d-flex align-items-center ${index === Object.keys(unitPreferences).length - 1 ? 'mb-4' : 'mb-1'}`}
                  >
                    <Col sm={8}>
                      <Form.Label className="font-weight-normal mb-0">
                        {intl.formatMessage({ id: measureClass })}
                      </Form.Label>
                    </Col>
                    <Col sm={4}>
                      <Dropdown
                        options={unitOptsForDropdown[measureClass]}
                        value={unitOptsForDropdown[measureClass].find(
                          (item) =>
                            item.value === newUnitPrefs[measureClass as keyof IUnitPreference]
                        )}
                        dataTestId={`settings_${measureClass}_dropdown`}
                        isSearchable={false}
                        onChange={(option: ISelectOption) =>
                          handleSelectUnit(option.value, measureClass)
                        }
                        className="w-175"
                        variant="form"
                      />
                    </Col>
                  </Row>
                )
              );
            })}
          </Form>
          {(settingsLoading || localeLoading) && <Loading />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" data-test="settings-cancel-btn" onClick={handleCloseModal}>
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            data-test="settings-save-btn"
            onClick={handleSubmit}
            disabled={
              (newLocale === locale &&
                defaultClinicalTimeLogTypeId === newTimeLogTypePref &&
                automaticClinicalTimeLog === newAutoTimeLogPref &&
                unitPreferences === newUnitPrefs) ||
              settingsLoading ||
              localeLoading ||
              saved
            }
          >
            {intl.formatMessage({ id: 'saveButtonTitle' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Settings;
